import React from 'react'
import { S as SInfoPopover } from 'features/booth/components/InfoPopover'
import Icon from 'components/Icon'
import { _ } from 'utils'
import styled from 'styled-components'
import { useDeleteItem, useEventSlug } from 'hooks'
import { useDispatch } from 'react-redux'
import { showNotification, showErrorNotification } from 'features/notifications/notificationsSlice'
import { useHistory } from 'react-router'
import routes from 'routes'
import ConfirmModal from 'components/ConfirmModal'
import { addModal } from 'features/modals/modalSlice'
import { deleteInvite, sendInviteEmails } from '../graphql/mutation'

const S = {
    PopoverMenu: styled.div<{ direction: string }>`
        position: absolute;
        right: 0;
        z-index: 1;
        background: white;
        box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
        border-radius: 2px;
        ${({ direction }) => (direction === 'up' ? `
        top: 0;
        transform: translateY(-100%);
        ` : `
        bottom: 0;
        transform: translateY(100%);
        `)}
    `,
    PopoverItem: SInfoPopover.PopoverItem
}

interface Props {
    id: number
    direction: string
    fetchData: () => void
    isAccepted?: boolean
}

const InfoPopover = ({
    id, direction, fetchData, isAccepted
}: Props) => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const history = useHistory()

    const handleDeleteInvite = async (_id) => {
        const response = await deleteInvite(_id)
        if (response.data) {
            fetchData()
            dispatch(showNotification({
                body: _('attendee.delete_invitation_success'),
                type: 'success'
            }))
            return
        }
        showErrorNotification(_('attendee.delete_invitation_failed'))
    }

    const { showDeleteConfirmModal } = useDeleteItem(
        handleDeleteInvite, null, {
            title: _('attendee.delete_attendee_header'),
            description: _('attendee.delete_attendee_description'),
        }
    )

    const sendInvite = async () => {
        const response = await sendInviteEmails(eventSlug, [id])
        if (response.data) {
            fetchData()
            dispatch(showNotification({
                body: _('attendee.send_invitation_success'),
                type: 'success'
            }))
            return
        }
        showErrorNotification(_('attendee.send_invitation_failed'))
    }

    const showConfirmModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <ConfirmModal
                    close={close}
                    title={_('attendee.send_invitation_modal_header')}
                    confirmDescription={_('attendee.send_invitation_modal_description')}
                    variantConfirm="primary"
                    onConfirm={sendInvite}
                />
            ),
            shouldNotWrap: true
        }))
    }

    const goToEditPage = () => {
        history.push(routes.updateAttendee.getPath({ eventSlug, id }))
    }

    return (
        <S.PopoverMenu direction={direction}>
            {!isAccepted && (
                <S.PopoverItem onClick={showConfirmModal}>
                    <Icon icon="emailSend" size={18} />
                    {_('attendee.list.send_invitation')}
                </S.PopoverItem>
            )}
            <S.PopoverItem onClick={goToEditPage}>
                <Icon icon="edit" size={18} />
                {_('attendee.list.edit_attendee')}
            </S.PopoverItem>
            <S.PopoverItem onClick={() => showDeleteConfirmModal(id)}>
                <Icon icon="trash" size={18} />
                {_('attendee.list.delete_attendee')}
            </S.PopoverItem>
        </S.PopoverMenu>
    )
}

export default InfoPopover
